<template>
  <div class="pb-10">
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-3">
        <button
          class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors"
          @click="handleCancel">
          <i class="fas fa-arrow-left text-sm"></i>
        </button>
        <div>
          <h1 class="text-lg font-semibold text-gray-900">Nouvelle commande d'achat</h1>
          <p class="text-xs text-gray-400">Créez une commande fournisseur et gérez votre stock</p>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          @click="handleCancel">
          <i class="fas fa-times text-[11px]"></i>
          Annuler
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isSubmitting || items.length === 0" @click="handleSubmit">
          <i v-if="isSubmitting" class="fas fa-spinner fa-spin text-[11px]"></i>
          <i v-else class="fas fa-check text-[11px]"></i>
          Enregistrer
        </button>
      </div>
    </div>

    <!-- Contenu principal -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-5">

      <!-- ===== COLONNE GAUCHE (2/3) ===== -->
      <div class="lg:col-span-2 space-y-5">

        <!-- Carte Fournisseur -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-visible">
          <div class="px-5 py-4 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-700">
              Fournisseur <span class="text-red-500">*</span>
            </h3>
          </div>
          <div class="p-5 space-y-4">
            <!-- Recherche fournisseur -->
            <div class="relative">
              <div class="relative">
                <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
                <input ref="supplierInput" type="text" v-model="supplierSearch"
                  placeholder="Rechercher un fournisseur..."
                  class="w-full pl-9 pr-10 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  @input="searchSuppliers" @focus="showSupplierDropdown = true" />
                <button v-if="supplierSearch"
                  class="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-300 hover:text-gray-500 rounded transition-colors"
                  @click="clearSupplierSelection">
                  <i class="fas fa-times text-[10px]"></i>
                </button>
              </div>

              <!-- Fournisseur sélectionné -->
              <div v-if="form.supplier_id"
                class="mt-2 flex items-center gap-3 p-3 bg-primary/5 border border-primary/15 rounded-lg">
                <div class="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-user text-primary text-[10px]"></i>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900 truncate">{{ supplierSearch }}</p>
                </div>
                <i class="fas fa-check text-primary text-xs"></i>
              </div>

              <!-- Dropdown fournisseurs -->
              <transition name="dropdown">
                <div v-if="showSupplierDropdown && supplierSuggestions.length > 0"
                  class="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto z-50">
                  <div v-for="s in supplierSuggestions" :key="s.id"
                    class="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50/60 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors"
                    :class="{ 'bg-primary/5': form.supplier_id === s.id }" @click="selectSupplier(s)">
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-gray-900 truncate">{{ s.name }}</p>
                      <p class="text-xs text-gray-400" v-if="s.phone || s.email">
                        <span v-if="s.phone"><i class="fas fa-phone mr-1 text-[10px]"></i>{{ s.phone }}</span>
                        <span v-if="s.email" class="ml-2"><i class="fas fa-envelope mr-1 text-[10px]"></i>{{ s.email }}</span>
                      </p>
                    </div>
                    <i v-if="form.supplier_id === s.id" class="fas fa-check text-primary text-xs flex-shrink-0"></i>
                  </div>
                </div>
              </transition>

              <!-- Aucun fournisseur trouvé -->
              <transition name="fade">
                <div v-if="showSupplierDropdown && supplierSuggestions.length === 0 && supplierSearch.trim()"
                  class="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 px-4 py-3 text-xs text-gray-400 text-center">
                  <i class="fas fa-circle-info mr-1"></i>
                  Aucun fournisseur trouvé
                </div>
              </transition>
            </div>

            <span v-if="errors.supplier_id" class="flex items-center gap-1.5 text-xs font-medium text-red-500">
              <i class="fas fa-circle-info text-[10px]"></i>
              {{ errors.supplier_id }}
            </span>

            <!-- Date et référence -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">
                  Date de commande <span class="text-red-500">*</span>
                </p>
                <input type="date" v-model="form.order_date"
                  class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" />
                <span v-if="errors.order_date" class="text-xs text-red-500 mt-1 block">
                  {{ errors.order_date }}
                </span>
              </div>
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Référence</p>
                <input type="text" v-model="form.reference" placeholder="Ex: PO-2025-0001"
                  class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" />
              </div>
            </div>

            <!-- Notes -->
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Remarques</p>
              <textarea v-model="form.notes" rows="2"
                placeholder="Notes additionnelles pour cette commande..."
                class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none"></textarea>
            </div>
          </div>
        </div>

        <!-- Carte Articles -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-visible">
          <div class="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 class="text-sm font-semibold text-gray-700">
              Articles <span class="text-gray-400 font-normal">({{ items.length }})</span>
            </h3>
            <div class="flex items-center gap-2">
              <input type="file" ref="csvFileInput" accept=".csv" class="hidden" @change="handleCsvFileChange" />
              <button
                class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                @click="triggerCsvImport" :disabled="isImporting">
                <i v-if="isImporting" class="fas fa-spinner fa-spin text-[10px]"></i>
                <i v-else class="fas fa-upload text-[10px]"></i>
                Importer CSV
              </button>
            </div>
          </div>

          <div class="p-5">
            <!-- Barre de recherche produit -->
            <div class="relative mb-4">
              <div class="flex gap-2">
                <div class="relative flex-1">
                  <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
                  <input type="text" v-model="globalSearch"
                    placeholder="Rechercher et ajouter un produit rapidement..."
                    class="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                    @input="onGlobalSearch" />
                  <span v-if="globalSuggestions.length > 0"
                    class="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary text-white">
                    {{ globalSuggestions.length }}
                  </span>
                </div>
                <button
                  class="inline-flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  @click="addLineFromGlobal" :disabled="!canAddFromGlobal">
                  <i class="fas fa-plus text-[10px]"></i>
                  Ajouter
                </button>
              </div>
            </div>

            <!-- Panier vide -->
            <div v-if="items.length === 0" class="py-12 text-center">
              <div class="text-gray-300 mb-3">
                <i class="fas fa-cart-shopping text-3xl"></i>
              </div>
              <p class="text-sm font-medium text-gray-500">Aucun article ajouté</p>
              <p class="text-xs text-gray-400 mt-0.5">Commencez par rechercher et ajouter des produits</p>
              <button
                class="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
                @click="addEmptyLine">
                <i class="fas fa-plus text-[10px]"></i>
                Ajouter le premier article
              </button>
            </div>

            <!-- Table articles -->
            <div v-else>
              <div class="overflow-x-auto rounded-lg border border-gray-200" style="min-height: 300px;">
                <table class="w-full min-w-[800px]">
                  <thead class="bg-gray-50/80">
                    <tr>
                      <th class="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Produit</th>
                      <th class="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-20">Qté</th>
                      <th class="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-28">Prix achat</th>
                      <th class="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-28">Prix public</th>
                      <th class="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-24">Remise</th>
                      <th class="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-28">Total</th>
                      <th class="px-3 py-2.5 w-10"></th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-50">
                    <tr v-for="(line, index) in items" :key="index" class="product-row hover:bg-gray-50/60 transition-colors">
                      <!-- Produit -->
                      <td class="px-3 py-2.5">
                        <div class="relative">
                          <div class="relative">
                            <i class="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300 text-[10px]"></i>
                            <input type="text" v-model="line.search"
                              :placeholder="line.product ? line.product.name : 'Rechercher...'"
                              class="w-full pl-7 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary transition-colors"
                              @input="searchProducts(index)" @focus="line.showDropdown = true" />
                          </div>
                          <transition name="dropdown">
                            <div v-if="line.showDropdown && line.suggestions.length"
                              class="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50">
                              <div v-for="p in line.suggestions" :key="p.id"
                                class="flex items-center justify-between px-3 py-2 hover:bg-gray-50/60 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors"
                                @click="selectProduct(index, p)">
                                <div class="flex-1 min-w-0">
                                  <p class="text-sm font-medium text-gray-900 truncate">{{ p.name }}</p>
                                  <p class="text-xs text-gray-400">
                                    <span class="font-semibold">#{{ p.id }}</span>
                                    <span v-if="p.reference" class="ml-1">Réf: {{ p.reference }}</span>
                                  </p>
                                </div>
                                <span v-if="p.price_excl_tax"
                                  class="ml-2 text-xs font-semibold text-primary bg-primary/5 px-1.5 py-0.5 rounded">
                                  {{ formatCurrency(p.price_excl_tax) }}
                                </span>
                              </div>
                            </div>
                          </transition>
                        </div>
                      </td>
                      <!-- Quantité -->
                      <td class="px-3 py-2.5">
                        <input type="number" min="1" step="1" v-model.number="line.quantity"
                          @input="normalizeNumber(line, 'quantity', 2)"
                          class="w-full px-2 py-1.5 text-sm text-right border border-gray-200 rounded-lg focus:outline-none focus:border-primary transition-colors" />
                      </td>
                      <!-- Prix achat -->
                      <td class="px-3 py-2.5">
                        <div class="relative">
                          <input type="number" min="0" step="1" v-model.number="line.purchase_price"
                            @input="normalizeNumber(line, 'purchase_price', 0)"
                            class="w-full px-2 pr-12 py-1.5 text-sm text-right border border-gray-200 rounded-lg focus:outline-none focus:border-primary transition-colors" />
                          <span class="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-gray-300 pointer-events-none">FCFA</span>
                        </div>
                      </td>
                      <!-- Prix public -->
                      <td class="px-3 py-2.5">
                        <div class="relative">
                          <input type="number" min="0" step="1" v-model.number="line.sale_price"
                            @input="normalizeNumber(line, 'sale_price', 0)"
                            class="w-full px-2 pr-12 py-1.5 text-sm text-right border border-gray-200 rounded-lg focus:outline-none focus:border-primary transition-colors" />
                          <span class="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-gray-300 pointer-events-none">FCFA</span>
                        </div>
                      </td>
                      <!-- Remise -->
                      <td class="px-3 py-2.5">
                        <div class="relative">
                          <input type="number" min="0" step="1" v-model.number="line.discount"
                            @input="normalizeNumber(line, 'discount', 0)"
                            class="w-full px-2 pr-12 py-1.5 text-sm text-right border border-gray-200 rounded-lg focus:outline-none focus:border-primary transition-colors" />
                          <span class="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-gray-300 pointer-events-none">FCFA</span>
                        </div>
                      </td>
                      <!-- Total -->
                      <td class="px-3 py-2.5 text-right">
                        <span class="text-sm font-bold text-gray-900">{{ formatCurrency(calculateLineTotal(line)) }}</span>
                      </td>
                      <!-- Supprimer -->
                      <td class="px-3 py-2.5 text-center">
                        <button
                          class="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          @click="confirmRemoveLine(index)" title="Supprimer">
                          <i class="fas fa-trash text-[10px]"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="mt-3 pt-3 border-t border-dashed border-gray-200">
                <button
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary border border-dashed border-primary/30 hover:bg-primary/5 rounded-lg transition-colors"
                  @click="addEmptyLine">
                  <i class="fas fa-plus text-[10px]"></i>
                  Ajouter une ligne
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== COLONNE DROITE (1/3) ===== -->
      <div class="space-y-5">

        <!-- Résumé -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden lg:sticky lg:top-5">
          <div :class="[
            'px-5 py-3 border-b',
            items.length > 0 ? 'bg-primary/5 border-primary/10' : 'bg-gray-50 border-gray-100'
          ]">
            <div class="flex items-center justify-between">
              <h3 :class="[
                'text-sm font-semibold',
                items.length > 0 ? 'text-primary' : 'text-gray-500'
              ]">Résumé</h3>
              <span v-if="items.length > 0"
                class="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-white/60 text-gray-500">
                {{ items.length }} article(s)
              </span>
            </div>
          </div>
          <div class="p-5 space-y-2.5">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Total prix d'achat</span>
              <span class="text-sm font-semibold text-gray-900">{{ formatCurrency(totalPurchase) }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Total prix public</span>
              <span class="text-sm font-medium text-gray-900">{{ formatCurrency(totalPublic) }}</span>
            </div>
            <div class="flex justify-between items-center pt-2.5 border-t border-gray-100">
              <span class="text-xs font-medium text-gray-500">Marge estimée</span>
              <span class="text-lg font-bold text-green-600">{{ formatCurrency(estimatedMargin) }}</span>
            </div>

            <div v-if="items.length > 0" class="flex justify-center pt-2">
              <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold" :class="{
                'bg-green-50 text-green-600': marginClass === 'high',
                'bg-amber-50 text-amber-600': marginClass === 'medium',
                'bg-red-50 text-red-500': marginClass === 'low'
              }">
                <i class="fas fa-chart-line text-[10px]"></i>
                {{ marginPercent }}%
              </span>
            </div>
          </div>

          <!-- Bouton enregistrer -->
          <div class="px-5 py-4 border-t border-gray-100">
            <button
              class="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isSubmitting || items.length === 0" @click="handleSubmit">
              <i v-if="isSubmitting" class="fas fa-spinner fa-spin text-xs"></i>
              <i v-else class="fas fa-check text-xs"></i>
              Enregistrer la commande
            </button>
          </div>
        </div>

        <!-- Indicateur de progression -->
        <div class="bg-white rounded-xl border border-gray-200 p-5">
          <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-3">Progression</p>
          <div class="space-y-3">
            <div class="flex items-center gap-3">
              <div :class="[
                'w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0',
                form.supplier_id ? 'bg-green-50 text-green-600' : 'bg-primary/10 text-primary'
              ]">
                <i v-if="form.supplier_id" class="fas fa-check text-[10px]"></i>
                <span v-else>1</span>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-xs font-semibold text-gray-900">Fournisseur</p>
                <p class="text-[11px] text-gray-400">Sélectionnez un fournisseur</p>
              </div>
            </div>
            <div class="ml-3.5 w-px h-3 bg-gray-200"></div>
            <div class="flex items-center gap-3">
              <div :class="[
                'w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0',
                items.length > 0 ? 'bg-green-50 text-green-600' : (form.supplier_id ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-400')
              ]">
                <i v-if="items.length > 0" class="fas fa-check text-[10px]"></i>
                <span v-else>2</span>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-xs font-semibold text-gray-900">Produits</p>
                <p class="text-[11px] text-gray-400">Ajoutez les articles</p>
              </div>
            </div>
            <div class="ml-3.5 w-px h-3 bg-gray-200"></div>
            <div class="flex items-center gap-3">
              <div :class="[
                'w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0',
                items.length > 0 ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-400'
              ]">
                3
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-xs font-semibold text-gray-900">Validation</p>
                <p class="text-[11px] text-gray-400">Finalisez la commande</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Annuler -->
    <transition name="modal">
      <div v-if="showCancelModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5" @click="showCancelModal = false">
        <div class="bg-white rounded-xl max-w-md w-full shadow-xl" @click.stop>
          <div class="p-6 text-center">
            <div class="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mx-auto mb-3">
              <i class="fas fa-exclamation-triangle text-amber-500"></i>
            </div>
            <h3 class="text-base font-semibold text-gray-900 mb-1">Annuler la commande ?</h3>
            <p class="text-xs text-gray-400">Toutes les informations saisies seront perdues.</p>
          </div>
          <div class="flex gap-3 px-6 pb-6">
            <button class="flex-1 px-3 py-2 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              @click="showCancelModal = false">
              Continuer l'édition
            </button>
            <button class="flex-1 px-3 py-2 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
              @click="confirmCancel">
              Oui, annuler
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal Supprimer article -->
    <transition name="modal">
      <div v-if="showDeleteModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5" @click="showDeleteModal = false">
        <div class="bg-white rounded-xl max-w-md w-full shadow-xl" @click.stop>
          <div class="p-6 text-center">
            <div class="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mx-auto mb-3">
              <i class="fas fa-trash text-red-500"></i>
            </div>
            <h3 class="text-base font-semibold text-gray-900 mb-1">Supprimer cet article ?</h3>
            <p class="text-xs text-gray-400">Voulez-vous vraiment supprimer cet article de la commande ?</p>
          </div>
          <div class="flex gap-3 px-6 pb-6">
            <button class="flex-1 px-3 py-2 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              @click="showDeleteModal = false">
              Annuler
            </button>
            <button class="flex-1 px-3 py-2 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
              @click="confirmDelete">
              Supprimer
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal Confirmer soumission -->
    <transition name="modal">
      <div v-if="showSubmitModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5" @click="showSubmitModal = false">
        <div class="bg-white rounded-xl max-w-md w-full shadow-xl" @click.stop>
          <div class="p-6 text-center">
            <div class="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center mx-auto mb-3">
              <i class="fas fa-check text-green-500"></i>
            </div>
            <h3 class="text-base font-semibold text-gray-900 mb-1">Enregistrer cette commande ?</h3>
          </div>
          <div class="px-6 pb-4">
            <div class="bg-gray-50 rounded-lg p-4 space-y-2.5">
              <div class="flex justify-between items-center">
                <span class="text-xs text-gray-400">Fournisseur</span>
                <span class="text-sm font-medium text-gray-900">{{ supplierSearch }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-xs text-gray-400">Articles</span>
                <span class="text-sm font-medium text-gray-900">{{ items.length }}</span>
              </div>
              <div class="flex justify-between items-center pt-2 border-t border-gray-200">
                <span class="text-xs text-gray-400">Total achat</span>
                <span class="text-sm font-bold text-primary">{{ formatCurrency(totalPurchase) }}</span>
              </div>
            </div>
          </div>
          <div class="flex gap-3 px-6 pb-6">
            <button class="flex-1 px-3 py-2 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              @click="showSubmitModal = false">
              Annuler
            </button>
            <button
              class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              @click="confirmSubmit" :disabled="isSubmitting">
              <i v-if="isSubmitting" class="fas fa-spinner fa-spin text-[10px]"></i>
              <span v-else>Confirmer</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal Import CSV -->
    <transition name="modal">
      <div v-if="showImportModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5" @click.self="closeImportModal">
        <div class="bg-white rounded-xl max-w-[900px] w-full max-h-[90vh] flex flex-col shadow-xl" @click.stop>
          <!-- Header -->
          <div class="flex items-start gap-4 px-6 py-5 border-b border-gray-200">
            <div class="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-file text-blue-500 text-sm"></i>
            </div>
            <div class="flex-1">
              <h3 class="text-base font-semibold text-gray-900">Produits à compléter</h3>
              <p class="text-xs text-gray-400 mt-0.5">
                {{ importNotFoundProducts.length }} produit{{ importNotFoundProducts.length > 1 ? 's' : '' }}
                non trouvé{{ importNotFoundProducts.length > 1 ? 's' : '' }} dans votre catalogue
              </p>
            </div>
            <button class="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-gray-500 border border-gray-200 rounded-lg transition-colors"
              @click="closeImportModal">
              <i class="fas fa-times text-xs"></i>
            </button>
          </div>

          <!-- Body -->
          <div class="flex-1 overflow-y-auto px-6 py-5 max-h-[60vh]">
            <div class="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-100 rounded-lg mb-5">
              <i class="fas fa-circle-info text-amber-500 text-xs flex-shrink-0"></i>
              <span class="text-xs font-medium text-amber-700">Ces produits seront créés comme nouveaux articles. Veuillez sélectionner une catégorie pour chacun.</span>
            </div>

            <div class="space-y-4">
              <div v-for="(product, index) in importNotFoundProducts" :key="index"
                class="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                <div class="flex items-center justify-between mb-3">
                  <span class="text-[11px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">#{{ index + 1 }}</span>
                  <button class="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    @click="removeImportProduct(index)" title="Supprimer">
                    <i class="fas fa-times text-[10px]"></i>
                  </button>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Nom <span class="text-red-500">*</span></p>
                    <input type="text" v-model="product.name" placeholder="Nom du produit"
                      class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-primary transition-colors" />
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Référence</p>
                    <input type="text" v-model="product.reference" placeholder="Référence"
                      class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-primary transition-colors" />
                  </div>
                  <div class="relative">
                    <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Catégorie <span class="text-red-500">*</span></p>
                    <div class="relative">
                      <i class="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300 text-[10px]"></i>
                      <input type="text" v-model="product.categorySearch"
                        placeholder="Rechercher..."
                        class="w-full pl-7 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-primary transition-colors"
                        @input="searchCategoriesForProduct(index)"
                        @focus="product.showCategoryDropdown = true" />
                      <button v-if="product.category_id"
                        class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 transition-colors"
                        @click="clearProductCategory(index)">
                        <i class="fas fa-times text-[10px]"></i>
                      </button>
                    </div>
                    <transition name="dropdown">
                      <div v-if="product.showCategoryDropdown && product.categorySuggestions.length > 0"
                        class="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto z-50">
                        <div v-for="cat in product.categorySuggestions" :key="cat.id"
                          class="flex items-center justify-between px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors"
                          :class="{ 'bg-primary/5': product.category_id === cat.id }"
                          @click="selectCategoryForProduct(index, cat)">
                          <span class="text-sm text-gray-900">{{ cat.name }}</span>
                          <i v-if="product.category_id === cat.id" class="fas fa-check text-primary text-[10px]"></i>
                        </div>
                      </div>
                    </transition>
                    <span v-if="!product.category_id && product.showError" class="text-[11px] text-red-500 mt-0.5 block">
                      Catégorie requise
                    </span>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Quantité</p>
                    <input type="number" v-model.number="product.quantity" min="1" placeholder="1"
                      class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-primary transition-colors" />
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Prix d'achat</p>
                    <div class="relative">
                      <input type="number" v-model.number="product.purchase_price" min="0" placeholder="0"
                        class="w-full px-3 pr-12 py-2 text-sm text-right border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-primary transition-colors" />
                      <span class="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-gray-300 pointer-events-none">FCFA</span>
                    </div>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Prix de vente</p>
                    <div class="relative">
                      <input type="number" v-model.number="product.sale_price" min="0" placeholder="0"
                        class="w-full px-3 pr-12 py-2 text-sm text-right border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-primary transition-colors" />
                      <span class="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-gray-300 pointer-events-none">FCFA</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <button class="px-4 py-2 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              @click="closeImportModal">
              Annuler
            </button>
            <button
              class="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              @click="confirmImportProducts" :disabled="importNotFoundProducts.length === 0">
              <i class="fas fa-plus text-[10px]"></i>
              Ajouter {{ importNotFoundProducts.length }} produit{{ importNotFoundProducts.length > 1 ? 's' : '' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
import { defineComponent, ref, computed } from 'vue'
import { productsService } from '@/services'
import supplierService from '@/services/supplierService'
import purchaseOrderService from '@/services/purchaseOrderService'
import categoriesService from '@/services/categoriesService'
import { useToastStore } from '@/store/modules/toastStore'
import { useRouter } from 'vue-router'
export default defineComponent({
  name: 'CreatePurchaseOrderView',
  setup() {
    const router = useRouter()
    const toastStore = useToastStore()

    const suppliers = ref([])
    const supplierSearch = ref('')
    const supplierSuggestions = ref([])
    const showSupplierDropdown = ref(false)
    const supplierInput = ref(null)
    const isSubmitting = ref(false)
    const errors = ref({})
    const form = ref({
      supplier_id: '',
      order_date: new Date().toISOString().slice(0, 10),
      reference: '',
      notes: ''
    })

    const items = ref([])
    const globalSearch = ref('')
    const globalSuggestions = ref([])
    const globalSearchTimer = ref(null)

    const showCancelModal = ref(false)
    const showDeleteModal = ref(false)
    const showSubmitModal = ref(false)
    const lineToDelete = ref(null)

    // CSV Import
    const csvFileInput = ref(null)
    const isImporting = ref(false)
    const showImportModal = ref(false)
    const importNotFoundProducts = ref([])
    const categories = ref([])
    const csvParsedData = ref([])

    const canAddFromGlobal = computed(() => globalSuggestions.value.length > 0)

    const totalPurchase = computed(() => {
      return items.value.reduce((sum, l) => {
        const qty = Number(l.quantity) || 0
        const price = Number(l.purchase_price) || 0
        const discount = Number(l.discount) || 0
        return sum + Math.max(0, qty * price - discount)
      }, 0)
    })

    const totalPublic = computed(() => {
      return items.value.reduce((sum, l) => {
        const qty = Number(l.quantity) || 0
        const price = Number(l.sale_price) || 0
        return sum + qty * price
      }, 0)
    })

    const estimatedMargin = computed(() => {
      return totalPublic.value - totalPurchase.value
    })

    const marginPercent = computed(() => {
      if (totalPublic.value === 0) return 0
      return ((estimatedMargin.value / totalPublic.value) * 100).toFixed(1)
    })

    const marginClass = computed(() => {
      const margin = parseFloat(marginPercent.value)
      if (margin >= 30) return 'high'
      if (margin >= 15) return 'medium'
      return 'low'
    })

    const calculateLineTotal = (line) => {
      const qty = Number(line.quantity) || 0
      const price = Number(line.purchase_price) || 0
      const discount = Number(line.discount) || 0
      return Math.max(0, qty * price - discount)
    }

    const loadSuppliers = async () => {
      try {
        const res = await supplierService.getSuppliers({ per_page: 100, active_only: true })
        const data = res.data
        suppliers.value = Array.isArray(data) ? data : (data.data || data.items || [])
        supplierSuggestions.value = suppliers.value
      } catch (e) {
        suppliers.value = []
        supplierSuggestions.value = []
      }
    }

    const loadCategories = async () => {
      try {
        const res = await categoriesService.getCategories({ per_page: 100 })
        const data = res.data
        categories.value = Array.isArray(data) ? data : (data.data || data.items || [])
      } catch (e) {
        categories.value = []
      }
    }

    // CSV Import Functions
    const triggerCsvImport = () => {
      if (csvFileInput.value) {
        csvFileInput.value.click()
      }
    }

    const handleCsvFileChange = async (event) => {
      const file = event.target.files[0]
      if (!file) return

      // Reset file input
      event.target.value = ''

      if (!file.name.endsWith('.csv')) {
        toastStore.addToast({
          type: 'error',
          message: 'Veuillez sélectionner un fichier CSV',
          duration: 3000
        })
        return
      }

      isImporting.value = true

      try {
        const csvContent = await readFileAsText(file)
        const parsedData = parseCsv(csvContent)

        if (parsedData.length === 0) {
          toastStore.addToast({
            type: 'error',
            message: 'Le fichier CSV est vide ou mal formaté',
            duration: 3000
          })
          return
        }

        csvParsedData.value = parsedData
        await processImport(parsedData)
      } catch (error) {
        toastStore.addToast({
          type: 'error',
          message: error.message || 'Erreur lors de la lecture du fichier CSV',
          duration: 4000
        })
      } finally {
        isImporting.value = false
      }
    }

    const readFileAsText = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = () => reject(new Error('Erreur de lecture du fichier'))
        reader.readAsText(file)
      })
    }

    const parseCsv = (content) => {
      const lines = content.split('\n').map(line => line.trim()).filter(line => line)
      if (lines.length < 2) return []

      // Parse header
      const header = lines[0].split(/[,;]/).map(h => h.trim().toLowerCase())

      // Find column indices
      const refIndex = header.findIndex(h => h === 'reference' || h === 'ref' || h === 'référence')
      const nameIndex = header.findIndex(h => h === 'nom' || h === 'name' || h === 'produit' || h === 'designation' || h === 'désignation')
      const purchasePriceIndex = header.findIndex(h => h === 'prix_achat' || h === 'prix achat' || h === 'purchase_price' || h === 'pa')
      const salePriceIndex = header.findIndex(h => h === 'prix_vente' || h === 'prix vente' || h === 'sale_price' || h === 'pv')
      const quantityIndex = header.findIndex(h => h === 'quantite' || h === 'quantité' || h === 'quantity' || h === 'qte' || h === 'qty')

      if (nameIndex === -1) {
        throw new Error('La colonne "nom" est requise dans le fichier CSV')
      }

      const result = []
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(/[,;]/).map(v => v.trim())

        const name = values[nameIndex] || ''
        if (!name) continue

        result.push({
          reference: refIndex !== -1 ? (values[refIndex] || null) : null,
          name: name,
          purchase_price: purchasePriceIndex !== -1 ? (parseFloat(values[purchasePriceIndex]) || null) : null,
          sale_price: salePriceIndex !== -1 ? (parseFloat(values[salePriceIndex]) || null) : null,
          quantity: quantityIndex !== -1 ? (parseInt(values[quantityIndex]) || 1) : 1
        })
      }

      return result
    }

    const processImport = async (parsedData) => {
      // Prepare references and names for API call
      const references = parsedData.filter(p => p.reference).map(p => p.reference)
      const names = parsedData.map(p => p.name)

      try {
        const response = await productsService.importProductsFromReferences({
          references: references.length > 0 ? references : undefined,
          names: names,
          with: ['category']
        })

        const data = response.data
        const foundProducts = data.products || []
        const summary = data.summary || {}

        // Add found products to items
        let addedCount = 0
        for (const product of foundProducts) {
          // Find the corresponding CSV data
          const csvItem = parsedData.find(p =>
            (p.reference && p.reference === product.reference) ||
            p.name.toLowerCase() === product.name.toLowerCase()
          )

          if (csvItem) {
            // Check if product already exists in items
            const existingIndex = items.value.findIndex(l => l.product_id === product.id)
            if (existingIndex !== -1) {
              // Update quantity
              items.value[existingIndex].quantity += csvItem.quantity
            } else {
              // Remove empty lines first
              const emptyLineIndex = items.value.findIndex(l => !l.product_id)
              if (emptyLineIndex !== -1) {
                items.value.splice(emptyLineIndex, 1)
              }

              // Add new line
              items.value.push({
                product: product,
                product_id: product.id,
                search: product.name,
                suggestions: [],
                showDropdown: false,
                quantity: csvItem.quantity,
                purchase_price: csvItem.purchase_price || product.price_excl_tax || 0,
                sale_price: csvItem.sale_price || product.price_incl_tax || 0,
                discount: 0
              })
              addedCount++
            }
          }
        }

        // Prepare not found products for modal
        const notFoundRefs = summary.not_found_references || []
        const notFoundNames = summary.not_found_names || []

        const notFoundItems = parsedData.filter(p => {
          if (p.reference && notFoundRefs.includes(p.reference)) return true
          if (notFoundNames.includes(p.name)) return true
          return false
        })

        if (notFoundItems.length > 0) {
          // Load categories if not loaded
          if (categories.value.length === 0) {
            await loadCategories()
          }

          importNotFoundProducts.value = notFoundItems.map(item => ({
            name: item.name,
            reference: item.reference || '',
            category_id: null,
            categorySearch: '',
            categorySuggestions: [...categories.value],
            showCategoryDropdown: false,
            showError: false,
            quantity: item.quantity || 1,
            purchase_price: item.purchase_price || 0,
            sale_price: item.sale_price || 0
          }))

          showImportModal.value = true
        }

        if (addedCount > 0) {
          toastStore.addToast({
            type: 'success',
            message: `${addedCount} produit${addedCount > 1 ? 's' : ''} ajouté${addedCount > 1 ? 's' : ''} à la commande`,
            duration: 3000
          })
        }

        if (notFoundItems.length > 0 && addedCount > 0) {
          toastStore.addToast({
            type: 'info',
            message: `${notFoundItems.length} produit${notFoundItems.length > 1 ? 's' : ''} nécessite${notFoundItems.length > 1 ? 'nt' : ''} votre attention`,
            duration: 3000
          })
        }

      } catch (error) {
        toastStore.addToast({
          type: 'error',
          message: error?.response?.data?.message || 'Erreur lors de l\'import des produits',
          duration: 4000
        })
      }
    }

    const searchCategoriesForProduct = (index) => {
      const product = importNotFoundProducts.value[index]
      if (!product) return

      const query = (product.categorySearch || '').trim().toLowerCase()
      if (!query) {
        product.categorySuggestions = [...categories.value]
      } else {
        product.categorySuggestions = categories.value.filter(cat =>
          cat.name.toLowerCase().includes(query)
        )
      }
      product.showCategoryDropdown = true
    }

    const selectCategoryForProduct = (index, category) => {
      const product = importNotFoundProducts.value[index]
      if (!product) return

      product.category_id = category.id
      product.categorySearch = category.name
      product.showCategoryDropdown = false
      product.showError = false
    }

    const clearProductCategory = (index) => {
      const product = importNotFoundProducts.value[index]
      if (!product) return

      product.category_id = null
      product.categorySearch = ''
      product.categorySuggestions = [...categories.value]
    }

    const removeImportProduct = (index) => {
      importNotFoundProducts.value.splice(index, 1)
      if (importNotFoundProducts.value.length === 0) {
        showImportModal.value = false
      }
    }

    const closeImportModal = () => {
      showImportModal.value = false
      importNotFoundProducts.value = []
    }

    const confirmImportProducts = () => {
      // Validate all products have categories
      let hasErrors = false
      for (const product of importNotFoundProducts.value) {
        if (!product.category_id) {
          product.showError = true
          hasErrors = true
        }
      }

      if (hasErrors) {
        toastStore.addToast({
          type: 'error',
          message: 'Veuillez sélectionner une catégorie pour tous les produits',
          duration: 3000
        })
        return
      }

      // Remove empty lines first
      const emptyLineIndex = items.value.findIndex(l => !l.product_id)
      if (emptyLineIndex !== -1) {
        items.value.splice(emptyLineIndex, 1)
      }

      // Add products as new items (they will be created when submitting the order)
      for (const product of importNotFoundProducts.value) {
        items.value.push({
          product: null,
          product_id: null,
          isNewProduct: true,
          newProductData: {
            name: product.name,
            reference: product.reference || null,
            category_id: product.category_id,
            price_excl_tax: product.purchase_price,
            price_incl_tax: product.sale_price
          },
          search: product.name,
          suggestions: [],
          showDropdown: false,
          quantity: product.quantity || 1,
          purchase_price: product.purchase_price || 0,
          sale_price: product.sale_price || 0,
          discount: 0
        })
      }

      toastStore.addToast({
        type: 'success',
        message: `${importNotFoundProducts.value.length} nouveau${importNotFoundProducts.value.length > 1 ? 'x' : ''} produit${importNotFoundProducts.value.length > 1 ? 's' : ''} ajouté${importNotFoundProducts.value.length > 1 ? 's' : ''}`,
        duration: 3000
      })

      closeImportModal()
      addEmptyLine()
    }

    const addEmptyLine = () => {
      items.value.push({
        product: null,
        product_id: null,
        search: '',
        suggestions: [],
        showDropdown: false,
        quantity: 1,
        purchase_price: 0,
        sale_price: 0,
        discount: 0
      })
    }

    const removeLine = (index) => {
      items.value.splice(index, 1)
    }

    const confirmRemoveLine = (index) => {
      lineToDelete.value = index
      showDeleteModal.value = true
    }

    const confirmDelete = () => {
      if (lineToDelete.value !== null) {
        removeLine(lineToDelete.value)
        lineToDelete.value = null
      }
      showDeleteModal.value = false
    }

    const scrollToLine = (index) => {
      setTimeout(() => {
        const rows = document.querySelectorAll('.product-row')
        if (rows && rows[index]) {
          rows[index].scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 0)
    }

    const searchProducts = (index) => {
      const line = items.value[index]
      if (!line) return
      const q = (line.search || '').trim()
      if (!q) {
        line.suggestions = []
        return
      }
      productsService.getProducts({
        search: q,
        per_page: 10
      }).then(res => {
        const data = res.data
        line.suggestions = Array.isArray(data) ? data : (data.data || [])
      }).catch(() => {
        line.suggestions = []
      }).finally(() => {
        line.showDropdown = true
      })
    }

    const selectProduct = (index, product) => {
      const line = items.value[index]
      if (!line) return
      const duplicateIndex = items.value.findIndex((l, i) => l.product_id === product.id && i !== index)
      if (duplicateIndex !== -1) {
        toastStore.addToast({
          type: 'warning',
          message: 'Ce produit est déjà ajouté dans la commande',
          duration: 3000
        })
        line.showDropdown = false
        line.suggestions = []
        line.search = ''
        scrollToLine(duplicateIndex)
        return
      }
      line.product = product
      line.product_id = product.id
      line.search = product.name
      line.showDropdown = false
      line.suggestions = []
      if (product.price_excl_tax !== undefined && product.price_excl_tax !== null) {
        line.purchase_price = Number(product.price_excl_tax)
      }
      if (product.price_incl_tax !== undefined && product.price_incl_tax !== null) {
        line.sale_price = Number(product.price_incl_tax)
      }
      addEmptyLine()
    }

    const onGlobalSearch = () => {
      if (globalSearchTimer.value) clearTimeout(globalSearchTimer.value)
      const q = (globalSearch.value || '').trim()
      if (!q) {
        globalSuggestions.value = []
        return
      }
      globalSearchTimer.value = setTimeout(() => {
        productsService.getProducts({ search: q, per_page: 10 })
          .then(res => {
            const data = res.data
            globalSuggestions.value = Array.isArray(data) ? data : (data.data || [])
          })
          .catch(() => { globalSuggestions.value = [] })
      }, 300)
    }

    const addLineFromGlobal = () => {
      if (!globalSuggestions.value.length) return
      const product = globalSuggestions.value[0]
      const existingIndex = items.value.findIndex(l => l.product_id === product.id)
      if (existingIndex !== -1) {
        toastStore.addToast({
          type: 'warning',
          message: 'Ce produit est déjà présent dans la commande',
          duration: 3000
        })
        scrollToLine(existingIndex)
        globalSearch.value = ''
        globalSuggestions.value = []
        return
      }
      addEmptyLine()
      selectProduct(items.value.length - 1, product)
      globalSearch.value = ''
      globalSuggestions.value = []
    }

    const searchSuppliers = () => {
      const query = (supplierSearch.value || '').trim()
      if (!query) {
        supplierSuggestions.value = suppliers.value
        return
      }
      supplierService.getSuppliers({
        search: query,
        per_page: 10,
        active_only: true
      }).then(res => {
        const data = res.data
        supplierSuggestions.value = Array.isArray(data) ? data : (data.data || [])
      }).catch(() => {
        supplierSuggestions.value = []
      })
    }

    const selectSupplier = (s) => {
      form.value.supplier_id = s.id
      supplierSearch.value = s.name
      showSupplierDropdown.value = false
    }

    const clearSupplierSelection = () => {
      form.value.supplier_id = ''
      supplierSearch.value = ''
      supplierSuggestions.value = suppliers.value
      showSupplierDropdown.value = false
      if (supplierInput.value) supplierInput.value.focus()
    }

    const normalizeNumber = (line, key, decimals) => {
      let val = Number(line[key])
      if (!isFinite(val) || val < 0) val = 0
      const factor = Math.pow(10, decimals)
      line[key] = Math.round(val * factor) / factor
    }

    const validate = () => {
      const e = {}
      if (!form.value.supplier_id) e.supplier_id = 'Le fournisseur est requis'
      if (!form.value.order_date) e.order_date = 'La date est requise'

      // Filter out empty lines (lines without product and without newProductData)
      const validItems = items.value.filter(l => l.product_id || l.isNewProduct)

      if (validItems.length === 0) {
        toastStore.addToast({ type: 'error', message: 'Ajoutez au moins une ligne', duration: 3000 })
        errors.value = e
        return false
      }

      let validLines = true
      for (const l of validItems) {
        // Check if it's a valid existing product or a valid new product
        const hasValidProduct = l.product_id || (l.isNewProduct && l.newProductData && l.newProductData.name && l.newProductData.category_id)
        if (!hasValidProduct || (Number(l.quantity) || 0) <= 0 || (Number(l.purchase_price) || 0) < 0) {
          validLines = false
          break
        }
      }
      if (!validLines) {
        toastStore.addToast({ type: 'error', message: 'Vérifiez les lignes (produit, quantité, prix)', duration: 3500 })
      }
      // Contrôle des doublons produits (only for existing products)
      const productIds = validItems.map(l => l.product_id).filter(Boolean)
      const hasDuplicates = new Set(productIds).size !== productIds.length
      if (hasDuplicates) {
        toastStore.addToast({ type: 'error', message: 'Vous avez des doublons de produits dans la commande', duration: 3500 })
      }
      errors.value = e
      return Object.keys(e).length === 0 && validLines && !hasDuplicates
    }

    const handleCancel = () => {
      if (items.value.length > 0 || form.value.supplier_id) {
        showCancelModal.value = true
      } else {
        router.push({ name: 'purchase-orders' })
      }
    }

    const confirmCancel = () => {
      showCancelModal.value = false
      router.push({ name: 'purchase-orders' })
    }

    const handleSubmit = () => {
      if (!validate()) return
      showSubmitModal.value = true
    }

    const confirmSubmit = async () => {
      if (isSubmitting.value) return
      isSubmitting.value = true
      try {
        // Filter out empty lines
        const validItems = items.value.filter(l => l.product_id || l.isNewProduct)

        const payload = {
          supplier_id: form.value.supplier_id,
          order_date: form.value.order_date,
          notes: form.value.notes || null,
          reference: form.value.reference || null,
          items: validItems.map(l => {
            if (l.isNewProduct && l.newProductData) {
              // New product to create
              return {
                is_new_product: true,
                new_product_data: {
                  name: l.newProductData.name,
                  reference: l.newProductData.reference || null,
                  category_id: l.newProductData.category_id,
                  price_excl_tax: Number(l.purchase_price) || 0,
                  price_incl_tax: Number(l.sale_price) || 0
                },
                quantity: Number(l.quantity),
                purchase_price: Number(l.purchase_price),
                sale_price: l.sale_price !== null && l.sale_price !== '' ? Number(l.sale_price) : null,
                discount: l.discount ? Number(l.discount) : 0
              }
            } else {
              // Existing product
              return {
                product_id: l.product_id,
                quantity: Number(l.quantity),
                purchase_price: Number(l.purchase_price),
                sale_price: l.sale_price !== null && l.sale_price !== '' ? Number(l.sale_price) : null,
                discount: l.discount ? Number(l.discount) : 0
              }
            }
          })
        }
        await purchaseOrderService.createPurchaseOrder(payload)
        toastStore.addToast({ type: 'success', message: 'Commande créée avec succès', duration: 4000 })
        showSubmitModal.value = false
        router.push({ name: 'purchase-orders' })
      } catch (err) {
        toastStore.addToast({
          type: 'error',
          message: err?.message || 'Erreur lors de la création de la commande',
          duration: 4000
        })
      } finally {
        isSubmitting.value = false
      }
    }

    loadSuppliers()
    addEmptyLine()

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount || 0) + ' FCFA'
    }

    return {
      suppliers,
      form,
      errors,
      isSubmitting,
      items,
      globalSearch,
      globalSuggestions,
      canAddFromGlobal,
      addLineFromGlobal,
      onGlobalSearch,
      addEmptyLine,
      removeLine,
      confirmRemoveLine,
      searchProducts,
      selectProduct,
      supplierInput,
      supplierSearch,
      supplierSuggestions,
      showSupplierDropdown,
      searchSuppliers,
      selectSupplier,
      clearSupplierSelection,
      normalizeNumber,
      totalPurchase,
      totalPublic,
      estimatedMargin,
      marginPercent,
      marginClass,
      calculateLineTotal,
      handleCancel,
      confirmCancel,
      handleSubmit,
      confirmSubmit,
      confirmDelete,
      formatCurrency,
      showCancelModal,
      showDeleteModal,
      showSubmitModal,
      scrollToLine,
      // CSV Import
      csvFileInput,
      isImporting,
      showImportModal,
      importNotFoundProducts,
      triggerCsvImport,
      handleCsvFileChange,
      searchCategoriesForProduct,
      selectCategoryForProduct,
      clearProductCategory,
      removeImportProduct,
      closeImportModal,
      confirmImportProducts
    }
  }
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
